import { Component } from '@theme/component';

/**
 * A tablist and its panels, wherever they appear.
 *
 * Knows nothing about what the panels contain: it shows one at a time, keeps `aria-selected`
 * and the roving tab stop honest, and announces the change. Anything that has to react to a
 * panel becoming visible listens for `tab-group:change` rather than being called from here.
 *
 * Buttons and panels are found by ref, so they need not be siblings or share a parent — a
 * design that renders every button in one row and every panel in another still works, as
 * long as both are inside this element and in matching order.
 *
 * Inert with no panels, so it is safe to wrap content that may or may not be tabbed.
 *
 * @example
 * <tab-group>
 *   <button role="tab" ref="tabButton[]" on:click="/activateTab"
 *           on:keydown:ArrowRight="/focusNextTab" on:keydown:ArrowLeft="/focusPreviousTab">…</button>
 *   <div role="tabpanel" ref="tabPanel[]" hidden>…</div>
 * </tab-group>
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} [tabButton] - Tab buttons, in order.
 * @property {HTMLElement[]} [tabPanel] - Tab panels, in the same order.
 *
 * @extends {Component<Refs>}
 */
class TabGroup extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.select(Math.max(this.selectedIndex, 0));
  }

  /** @returns {number} The open tab's position, or -1 before one is opened. */
  get selectedIndex() {
    return (this.refs.tabButton ?? []).findIndex((button) => button.getAttribute('aria-selected') === 'true');
  }

  /**
   * Open the tab whose panel contains `element`, if any.
   *
   * Lets a caller say "show me this" without knowing which tab holds it — a preselected
   * style, or a url naming one that happens to sit in a later tab.
   *
   * @param {Element | null} element
   * @returns {boolean} Whether a tab was found to open.
   */
  reveal(element) {
    if (!element) return false;

    const index = (this.refs.tabPanel ?? []).findIndex((panel) => panel.contains(element));
    if (index === -1) return false;

    this.select(index);

    return true;
  }

  /**
   * Called by the on:click binding on each tab button.
   * @param {MouseEvent} event
   */
  activateTab(event) {
    const index = (this.refs.tabButton ?? []).indexOf(/** @type {HTMLButtonElement} */ (event.target));

    if (index !== -1) this.select(index);
  }

  /** @param {KeyboardEvent} event */
  focusNextTab(event) {
    this.#moveFocus(event, 1);
  }

  /** @param {KeyboardEvent} event */
  focusPreviousTab(event) {
    this.#moveFocus(event, -1);
  }

  /**
   * Move selection and focus by `offset`, wrapping at both ends. Required once
   * `role="tablist"` is declared — a tablist is a single tab stop, so the arrow keys are
   * the only way through it.
   *
   * @param {KeyboardEvent} event
   * @param {number} offset
   */
  #moveFocus(event, offset) {
    const buttons = this.refs.tabButton ?? [];
    const current = buttons.indexOf(/** @type {HTMLButtonElement} */ (event.target));
    if (current === -1) return;

    event.preventDefault();
    const index = (current + offset + buttons.length) % buttons.length;

    this.select(index);
    buttons[index]?.focus();
  }

  /**
   * Show one panel, and make its button the group's only focus stop.
   *
   * @param {number} index - Tab position.
   */
  select(index) {
    const panels = this.refs.tabPanel ?? [];
    if (panels.length === 0 || index < 0 || index >= panels.length) return;

    (this.refs.tabButton ?? []).forEach((button, i) => {
      const selected = i === index;
      button.setAttribute('aria-selected', selected ? 'true' : 'false');
      button.tabIndex = selected ? 0 : -1;
    });

    panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });

    this.dispatchEvent(
      new CustomEvent('tab-group:change', { bubbles: true, detail: { index, panel: panels[index] } })
    );
  }
}

if (!customElements.get('tab-group')) {
  customElements.define('tab-group', TabGroup);
}
